// Extend Window interface to include grecaptcha
declare global {
  interface Window {
    grecaptcha?: any;
  }
}

const DEFAULT_CLIENT_KEY = process.env.VUE_APP_RECAPTCHA_V3_SITEKEY || "";

export default class Recaptcha {
  recaptchaLoaded = false;
  isRecaptchaReady = false;
  clientKey = "";

  constructor(clientKey: string = DEFAULT_CLIENT_KEY) {
    this.clientKey = clientKey;
  }

  loadRecaptchaScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (window.grecaptcha) {
        resolve();
        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector(
        'script[src*="recaptcha/api.js"]'
      );
      if (existingScript) {
        // Wait for the existing script to load
        const checkInterval = setInterval(() => {
          if (window.grecaptcha) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        return;
      }

      // Create and append script tag
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.clientKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        reject(new Error("Failed to load reCAPTCHA script"));
      };

      document.head.appendChild(script);
    });
  };

  // Check reCAPTCHA status
  checkRecaptchaStatus = () => {
    if (typeof window !== "undefined") {
      const scriptLoaded = !!window.grecaptcha;
      const executeMethod = !!(window.grecaptcha && window.grecaptcha.execute);
      const readyMethod = !!(window.grecaptcha && window.grecaptcha.ready);

      this.recaptchaLoaded = scriptLoaded && executeMethod && readyMethod;
      this.isRecaptchaReady = this.recaptchaLoaded;
    }

    return this.recaptchaLoaded;
  };

  // Initialize reCAPTCHA status checking
  initRecaptchaCheck = () => {
    // Check immediately
    this.checkRecaptchaStatus();

    // Check periodically until loaded
    const checkInterval = setInterval(() => {
      this.checkRecaptchaStatus();
      if (this.recaptchaLoaded) {
        clearInterval(checkInterval);
      }
    }, 1000);

    // Stop checking after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!this.recaptchaLoaded) {
        console.warn("reCAPTCHA failed to load within 10 seconds");
      }
    }, 10000);
  };

  // Execute reCAPTCHA challenge
  executeRecaptcha = (action = "submit") => {
    return new Promise((resolve: (token: string) => void, reject) => {
      if (!window.grecaptcha || !window.grecaptcha.ready) {
        const error =
          "reCAPTCHA not loaded. Please refresh the page and try again.";
        reject(new Error(error));
        return;
      }

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(this.clientKey, { action })
          .then((token: string) => {
            resolve(token);
          })
          .catch((error: string) => {
            console.error("reCAPTCHA execution failed:", error);
            reject(error);
          });
      });
    });
  };

  // Get debug info
  getDebugInfo = () => {
    return {
      recaptchaLoaded: this.recaptchaLoaded,
      isRecaptchaReady: this.isRecaptchaReady,
      siteKey: this.clientKey?.substring(0, 20) + "...",
      hasGrecaptcha: typeof window !== "undefined" && !!window.grecaptcha
    };
  };
}

export const recaptchaV3 = new Recaptcha();

export const mountRecaptchaV3 = async () => {
  if (typeof window === "undefined") return;
  await recaptchaV3.loadRecaptchaScript();
  recaptchaV3.initRecaptchaCheck();
  return recaptchaV3;
}